import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import MetricsCard from './components/MetricsCard';
import PartnerAcquisitionChart from './components/PartnerAcquisitionChart';
import CommissionDistributionChart from './components/CommissionDistributionChart';
import PendingApprovals from './components/PendingApprovals';
import SystemAlerts from './components/SystemAlerts';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import TopPerformers from './components/TopPerformers';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AdminDashboard = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRefreshing(false);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const formatDateTime = (date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const metricsData = [
    {
      title: 'Total Partenaires',
      value: 1247,
      change: '+12%',
      changeType: 'positive',
      icon: 'Users',
      currency: false
    },
    {
      title: 'Campagnes Actives',
      value: 89,
      change: '+5%',
      changeType: 'positive',
      icon: 'Calendar',
      currency: false
    },
    {
      title: 'Commissions Payées',
      value: 125750,
      change: '+18%',
      changeType: 'positive',
      icon: 'CreditCard',
      currency: true
    },
    {
      title: 'Croissance Mensuelle',
      value: 23,
      change: '+8%',
      changeType: 'positive',
      icon: 'TrendingUp',
      currency: false
    }
  ];

  const dateRangeOptions = [
    { value: '7d', label: '7 jours' },
    { value: '30d', label: '30 jours' },
    { value: '90d', label: '90 jours' },
    { value: '1y', label: '1 an' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Tableau de Bord Administrateur</h1>
                <p className="text-muted-foreground">
                  {formatDateTime(currentTime)}
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Date Range Selector */}
                <div className="flex items-center space-x-2 bg-card border border-border rounded-lg p-1">
                  {dateRangeOptions?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => handleDateRangeChange(option?.value)}
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        dateRange === option?.value
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  loading={refreshing}
                >
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Actualiser
                </Button>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metricsData?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                currency={metric?.currency}
              />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <PartnerAcquisitionChart />
            <CommissionDistributionChart />
          </div>

          {/* Management Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1">
              <PendingApprovals />
            </div>
            <div className="lg:col-span-1">
              <SystemAlerts />
            </div>
            <div className="lg:col-span-1">
              <RecentActivity />
            </div>
          </div>

          {/* Additional Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <QuickActions />
            <TopPerformers />
          </div>

          {/* Footer Stats */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg mx-auto mb-3">
                  <Icon name="DollarSign" size={24} className="text-success" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">
                  {new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0
                  })?.format(2450000)}
                </div>
                <p className="text-sm text-muted-foreground">Revenus Totaux</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg mx-auto mb-3">
                  <Icon name="Users" size={24} className="text-accent" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">
                  {new Intl.NumberFormat('fr-FR')?.format(15420)}
                </div>
                <p className="text-sm text-muted-foreground">Bookings Totaux</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg mx-auto mb-3">
                  <Icon name="Calendar" size={24} className="text-warning" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">
                  {new Intl.NumberFormat('fr-FR')?.format(342)}
                </div>
                <p className="text-sm text-muted-foreground">Événements Organisés</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-error/10 rounded-lg mx-auto mb-3">
                  <Icon name="Star" size={24} className="text-error" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">4,8</div>
                <p className="text-sm text-muted-foreground">Note Moyenne</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;